/**
 * A mutation's outcome, read from the server — never assumed.
 *
 * RTK Query mutations resolve (they do not throw) on failure, so
 * `await mutate(args)` followed by "close the form" / "navigate away" acts as
 * if the request succeeded when it did not. `mutationOutcome` unwraps the
 * call, and on failure hands the server's message (or the fallback) to
 * `report` and returns `{ ok: false }` so the caller keeps its state — a form
 * stays open, a toggle stays where the server has it — and the user is told.
 */

import { getErrorMessage } from './errorHelpers';

export type MutationOutcome<T> = { ok: true; data: T } | { ok: false; message: string };

export async function mutationOutcome<T>(
  call: { unwrap: () => Promise<T> },
  fallbackMessage: string,
  report?: (message: string) => void,
): Promise<MutationOutcome<T>> {
  try {
    const data = await call.unwrap();
    return { ok: true, data };
  } catch (err) {
    const message = getErrorMessage(err, fallbackMessage);
    report?.(message);
    return { ok: false, message };
  }
}
